import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Image, Loader2 } from 'lucide-react';
import { api, type PortfolioItem } from '../../lib/api';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '../ui/responsive-modal';

interface PortfolioGalleryProps {
  professionalId: string;
}

export function PortfolioGallery({ professionalId }: PortfolioGalleryProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<PortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const { items } = await api.getPortfolio(professionalId);
        setItems(items);
      } catch (error) {
        console.error('Failed to fetch portfolio:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPortfolio();
  }, [professionalId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Image size={32} className="mx-auto mb-2 opacity-50" />
        <p>{t('portfolio.empty', 'Nenhum item no portfólio ainda')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelectedItem(item)}
            className="group relative aspect-square rounded-lg overflow-hidden bg-muted border border-border hover:border-primary/50 transition-colors"
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Image size={32} className="text-muted-foreground/50" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white text-sm font-medium truncate">{item.title}</p>
                {item.category && (
                  <p className="text-white/70 text-xs">{item.category}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <ResponsiveModal
        open={!!selectedItem}
        onOpenChange={(o) => !o && setSelectedItem(null)}
      >
        <ResponsiveModalContent className="overflow-hidden p-0 sm:max-w-2xl">
          {selectedItem && (
            <>
              <ResponsiveModalHeader className="sr-only">
                <ResponsiveModalTitle>{selectedItem.title}</ResponsiveModalTitle>
              </ResponsiveModalHeader>
              {selectedItem.imageUrl && (
                <img
                  src={selectedItem.imageUrl}
                  alt={selectedItem.title}
                  className="max-h-[60vh] w-full bg-black object-contain"
                />
              )}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-foreground">{selectedItem.title}</h3>
                {selectedItem.category && (
                  <p className="mt-1 text-sm text-primary">{selectedItem.category}</p>
                )}
                {selectedItem.description && (
                  <p className="mt-2 text-muted-foreground">{selectedItem.description}</p>
                )}
                {selectedItem.projectUrl && (
                  <a
                    href={selectedItem.projectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink size={14} />
                    {t('portfolio.viewProject', 'Ver Projeto')}
                  </a>
                )}
              </div>
            </>
          )}
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  );
}
